import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  computeEndDateAsync,
  fetchSuggestedStartDateAsync,
  type StudentSubscription,
} from '../store/slices/studentsSlice';
import SubscriptionPeriodPicker, { type SubscriptionBusyPeriod } from './SubscriptionPeriodPicker';
import { formatDateEuropean } from '../utils/dateUtils';
import './RewardSubscriptionBox.css';
import './GroupForm.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function normYmd(s: string | null | undefined): string {
  if (!s) return '';
  return s.includes('T') ? s.split('T')[0] : s;
}

type Kind = 'referral' | 'mention';

type Props = {
  kind: Kind;
  /** Student who receives the zero-price pack */
  recipientStudentId: number;
  recipientName: string;
  /** Required for referral create/update */
  referredStudentId?: number;
  referredFirstName?: string;
  existing?: StudentSubscription | null;
  giveawayName?: string;
  onSuccess: () => void;
  onDismiss?: () => void;
};

const RewardSubscriptionBox: React.FC<Props> = ({
  kind,
  recipientStudentId,
  recipientName,
  referredStudentId,
  referredFirstName,
  existing,
  giveawayName,
  onSuccess,
  onDismiss,
}) => {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth?.token);

  const defaultLessons = existing?.lessonsCount ?? (kind === 'referral' ? 4 : 1);
  const typeLabel = kind === 'referral' ? 'Referral' : 'Mention';

  const [lessonsCount, setLessonsCount] = useState(String(defaultLessons));
  const [startDate, setStartDate] = useState(normYmd(existing?.startDate) || '');
  const [paymentDate, setPaymentDate] = useState(normYmd(existing?.paymentDate) || todayYmd());
  const [endDate, setEndDate] = useState(normYmd(existing?.endDate) || '');
  const [isEndDateManual, setIsEndDateManual] = useState(Boolean(existing?.endDate));
  const [estimatedEndDate, setEstimatedEndDate] = useState<string | null>(existing?.estimatedEndDate ?? null);
  const [busyPeriods, setBusyPeriods] = useState<SubscriptionBusyPeriod[]>([]);
  const [showAdjust, setShowAdjust] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(existing?.proofUrl ?? null);
  const [accountEligible, setAccountEligible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!existing && recipientStudentId) {
      dispatch(fetchSuggestedStartDateAsync({ studentId: recipientStudentId }))
        .unwrap()
        .then((payload) => {
          if (payload) setStartDate(payload);
          else setStartDate((prev) => prev || todayYmd());
        })
        .catch(() => setStartDate((prev) => prev || todayYmd()));
    }
  }, [dispatch, existing, recipientStudentId]);

  useEffect(() => {
    if (!recipientStudentId || !token) return;
    axios.get(`${API_BASE_URL}/students/${recipientStudentId}/subscriptions`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      const subs = (res.data.subscriptions || []) as StudentSubscription[];
      setBusyPeriods(
        subs
          .filter((s) => s.id !== existing?.id)
          .map((s) => {
            const end = s.effectiveEndDate ?? s.endDate ?? s.estimatedEndDate;
            if (!s.startDate || !end) return null;
            return { start: normYmd(s.startDate), end: normYmd(end) };
          })
          .filter((p): p is SubscriptionBusyPeriod => p != null)
      );
    }).catch(() => setBusyPeriods([]));
  }, [recipientStudentId, token, existing?.id]);

  useEffect(() => {
    const count = parseInt(lessonsCount, 10);
    if (!recipientStudentId || !startDate || !count || isEndDateManual) return;
    const t = setTimeout(() => {
      dispatch(computeEndDateAsync({
        studentId: recipientStudentId,
        startDate,
        lessonsCount: count,
      })).unwrap()
        .then((r) => setEstimatedEndDate(r.estimatedEndDate))
        .catch(() => setEstimatedEndDate(null));
    }, 250);
    return () => clearTimeout(t);
  }, [dispatch, recipientStudentId, startDate, lessonsCount, isEndDateManual]);

  const onPickFile = (f: File | null) => {
    setFile(f);
    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(f ? URL.createObjectURL(f) : existing?.proofUrl ?? null);
  };

  const handlePeriodChange = useCallback((next: { startDate: string; endDate: string }) => {
    setStartDate(next.startDate);
    setEndDate(next.endDate);
    if (next.endDate && next.endDate !== estimatedEndDate) {
      setIsEndDateManual(true);
    } else if (!next.endDate) {
      setIsEndDateManual(false);
    }
  }, [estimatedEndDate]);

  const needsAccountCheck = kind === 'mention' && !existing;
  const canSubmit = Boolean(file || existing?.proofUrl) && Boolean(startDate) && parseInt(lessonsCount, 10) >= 1
    && (!needsAccountCheck || accountEligible);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (kind === 'referral' && !referredStudentId) {
      setError('Referred student is missing');
      return;
    }
    setSubmitting(true);
    setError(null);
    const fd = new FormData();
    if (file) fd.append('file', file);
    fd.append('lessonsCount', String(parseInt(lessonsCount, 10)));
    fd.append('startDate', startDate);
    fd.append('paymentDate', paymentDate);
    if (isEndDateManual && endDate) fd.append('endDate', endDate);
    if (kind === 'referral') fd.append('referrerStudentId', String(recipientStudentId));
    if (kind === 'mention' && !existing) fd.append('accountEligibleConfirmed', accountEligible ? 'true' : 'false');

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      if (kind === 'referral' && referredStudentId) {
        const url = existing
          ? `${API_BASE_URL}/students/${referredStudentId}/referral/${existing.id}`
          : `${API_BASE_URL}/students/${referredStudentId}/referral`;
        await axios.request({ url, method: existing ? 'put' : 'post', data: fd, headers });
      } else {
        const url = existing
          ? `${API_BASE_URL}/students/${recipientStudentId}/mention-reward/${existing.id}`
          : `${API_BASE_URL}/students/${recipientStudentId}/mention-reward`;
        await axios.request({ url, method: existing ? 'put' : 'post', data: fd, headers });
      }
      onSuccess();
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string })?.error || err.message
        : 'Failed to add reward subscription';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const lessonN = parseInt(lessonsCount, 10) || defaultLessons;
  const copy = kind === 'referral'
    ? <>This will add a <strong>{lessonN}-lesson</strong> reward subscription to <strong>{recipientName}</strong> (the student who brought {referredFirstName || 'this student'}). Price <strong>0 BYN</strong>. A screenshot of the link (Telegram, a story, anything you used) is required.</>
    : <>This will add a <strong>{lessonN}-lesson</strong> reward subscription to <strong>{recipientName}</strong> for a mention of the English profile{giveawayName ? <> in <strong>{giveawayName}</strong></> : null}. Price <strong>0 BYN</strong>. Screenshot required — the story or post must still be there when you check; a shot that was deleted a minute later does not count.</>;

  return (
    <div className="reward-box">
      <p className="reward-box__copy">{copy}</p>

      <div className="reward-box__meta">
        <span>Type: <strong>{typeLabel}</strong></span>
        <span>Price: <strong>0 BYN</strong></span>
        <span>Student: <strong>{recipientName}</strong></span>
      </div>

      <div className="reward-box__proof">
        {previewUrl && (
          <img src={previewUrl} alt="Proof screenshot" className="reward-box__preview" />
        )}
        <div className="reward-box__proof-control">
          <label className="btn-secondary reward-box__file-button">
            {previewUrl ? 'Replace screenshot' : 'Choose screenshot'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <span className="reward-box__file-name">
            {file?.name || (existing?.proofUrl ? 'Current proof attached' : 'JPEG, PNG or WebP · max 5 MB')}
          </span>
        </div>
      </div>

      {needsAccountCheck && (
        <label className="checkbox-row reward-box__account">
          <input
            type="checkbox"
            checked={accountEligible}
            onChange={(event) => setAccountEligible(event.target.checked)}
          />
          <span>Instagram account has at least 100 followers and a few posts (not an empty profile)</span>
        </label>
      )}

      <button type="button" className="btn-secondary" onClick={() => setShowAdjust((v) => !v)}>
        {showAdjust ? 'Hide adjustment' : 'Adjust subscription'}
      </button>

      {showAdjust && (
        <div className="reward-box__adjust">
          <div className="form-row-compact">
            <div className="form-group">
              <label>Lessons *</label>
              <input
                type="number"
                min={1}
                className="form-input"
                value={lessonsCount}
                onChange={(e) => setLessonsCount(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Payment date *</label>
              <input
                type="date"
                className="form-input"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
          </div>
          <SubscriptionPeriodPicker
            startDate={startDate}
            endDate={endDate}
            onChange={handlePeriodChange}
            busyPeriods={busyPeriods}
          />
          <p className="reward-box__end">
            End date:{' '}
            {isEndDateManual
              ? `${endDate ? formatDateEuropean(endDate) : '—'} (manual)`
              : estimatedEndDate
                ? `${formatDateEuropean(estimatedEndDate)} (auto)`
                : '—'}
          </p>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="reward-box__actions">
        {onDismiss && (
          <button type="button" className="btn-secondary" onClick={onDismiss} disabled={submitting}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="btn-success"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting
            ? 'Saving…'
            : existing
              ? `Update ${lessonN}-lesson subscription`
              : `Add ${lessonN}-lesson subscription`}
        </button>
      </div>
    </div>
  );
};

export default RewardSubscriptionBox;
